import { Anchor, ArrowLeft, Ban, CheckCircle2, Pencil, Play } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { QrCodeDisplay } from '@/components/ui/qr-code-display';
import { Skeleton } from '@/components/ui/skeleton';
import { TransitionModal } from '@/components/ui/transition-modal';
import {
  statusVisualTone as certificateStatusTone,
  type Certificate,
  type CertificateStatus,
} from '@/features/certificates/types';
import {
  useCancelDrydockCampaign,
  useCompleteDrydockCampaign,
  useDrydockCampaign,
  useDrydockCampaignCertificates,
  useDrydockCampaignWorkOrders,
  useStartDrydockCampaign,
} from '@/features/drydock-campaigns/queries';
import {
  statusVisualTone,
  type DrydockCampaign,
  type DrydockCampaignStatus,
} from '@/features/drydock-campaigns/types';
import { useVessel } from '@/features/vessels/queries';
import { vesselDisplayName } from '@/features/vessels/types';
import {
  workOrderStatusColor,
  type WorkOrder,
} from '@/features/work-orders/types';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

const STATUS_TONE_CLASS: Record<
  ReturnType<typeof statusVisualTone>,
  string
> = {
  emerald: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-700',
  muted: 'bg-slate-100 text-slate-700',
};

const CERT_STATUS_TONE_CLASS: Record<
  ReturnType<typeof certificateStatusTone>,
  string
> = STATUS_TONE_CLASS;

function StatusBadge({
  status,
  label,
}: {
  status: DrydockCampaignStatus;
  label: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
        STATUS_TONE_CLASS[statusVisualTone(status)],
      )}
    >
      {label}
    </span>
  );
}

function CertStatusBadge({
  status,
  label,
}: {
  status: CertificateStatus;
  label: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
        CERT_STATUS_TONE_CLASS[certificateStatusTone(status)],
      )}
    >
      {label}
    </span>
  );
}

function Field({
  label,
  value,
  fullWidth = false,
}: {
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}) {
  const display =
    value === null || value === undefined || value === '' ? '—' : value;
  return (
    <div className={cn(fullWidth && 'sm:col-span-2')}>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm">{display}</dd>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2">{children}</dl>
      </CardContent>
    </Card>
  );
}

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function formatDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function formatBudget(
  amount: string | null,
  currency: string | null,
): string | null {
  if (!amount || !currency) return null;
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${amount} ${currency}`;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(n);
  } catch {
    return `${n.toLocaleString()} ${currency}`;
  }
}

function VesselField({ vesselId }: { vesselId: string }) {
  const { t } = useTranslation();
  const { data: vessel } = useVessel(vesselId);
  return (
    <Field
      label={t('drydockCampaign.fields.vessel')}
      value={
        <Link
          to={`/vessels/${vesselId}`}
          className="text-primary hover:underline"
        >
          {vessel ? vesselDisplayName(vessel) : vesselId}
        </Link>
      }
    />
  );
}

/**
 * Linked work orders card. Empty-state when the backend returns []
 * — a campaign without WO yet is the common case while the user is
 * still planning. Each row is a clickable link to the WO detail page.
 */
function LinkedWorkOrdersCard({ campaignId }: { campaignId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useDrydockCampaignWorkOrders(campaignId);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t('drydockCampaign.detail.sectionWorkOrders')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading && <Skeleton className="h-16 w-full" />}
        {!isLoading && (data ?? []).length === 0 && (
          <p className="text-sm text-muted-foreground">
            {t('drydockCampaign.detail.noWorkOrders')}
          </p>
        )}
        {(data ?? []).map((wo: WorkOrder) => (
          <Link
            key={wo.id}
            to={`/work-orders/${wo.id}`}
            className="flex items-center justify-between rounded-md border p-3 hover:bg-secondary"
          >
            <div className="min-w-0 flex-1">
              <p className="font-mono text-xs text-muted-foreground">
                {wo.number}
              </p>
              <p className="truncate text-sm font-medium">{wo.title}</p>
            </div>
            <span
              className={cn(
                'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                workOrderStatusColor(wo.status),
              )}
            >
              {t(`workOrder.status.${wo.status}`)}
            </span>
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

function LinkedCertificatesCard({ campaignId }: { campaignId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useDrydockCampaignCertificates(campaignId);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t('drydockCampaign.detail.sectionCertificates')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading && <Skeleton className="h-16 w-full" />}
        {!isLoading && (data ?? []).length === 0 && (
          <p className="text-sm text-muted-foreground">
            {t('drydockCampaign.detail.noCertificates')}
          </p>
        )}
        {(data ?? []).map((c: Certificate) => (
          <Link
            key={c.id}
            to={`/certificates/${c.id}`}
            className="flex items-center justify-between rounded-md border p-3 hover:bg-secondary"
          >
            <div className="min-w-0 flex-1">
              <p className="font-mono text-xs text-muted-foreground">
                {c.certificateNumber}
              </p>
              <p className="truncate text-sm font-medium">{c.title}</p>
            </div>
            <CertStatusBadge
              status={c.status}
              label={t(`certificate.status.${c.status}`)}
            />
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

function DetailContent({ campaign }: { campaign: DrydockCampaign }) {
  const { t } = useTranslation();
  const budget = formatBudget(
    campaign.totalBudgetAmount,
    campaign.totalBudgetCurrency,
  );

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title={t('drydockCampaign.detail.sectionIdentity')}>
        <Field
          label={t('drydockCampaign.fields.title')}
          value={<span className="font-medium">{campaign.title}</span>}
          fullWidth
        />
        <VesselField vesselId={campaign.vesselId} />
        <Field
          label={t('drydockCampaign.fields.status')}
          value={
            <StatusBadge
              status={campaign.status}
              label={t(`drydockCampaign.status.${campaign.status}`)}
            />
          }
        />
        <Field
          label={t('drydockCampaign.fields.shipyard')}
          value={campaign.shipyard}
        />
        <Field
          label={t('drydockCampaign.fields.location')}
          value={campaign.location}
        />
        <Field
          label={t('drydockCampaign.fields.description')}
          value={
            campaign.description ? (
              <p className="whitespace-pre-line">{campaign.description}</p>
            ) : null
          }
          fullWidth
        />
      </Section>

      <Section title={t('drydockCampaign.detail.sectionPlanning')}>
        <Field
          label={t('drydockCampaign.fields.startDate')}
          value={formatDate(campaign.startDate)}
        />
        <Field
          label={t('drydockCampaign.fields.endDate')}
          value={formatDate(campaign.endDate)}
        />
        <Field
          label={t('drydockCampaign.fields.totalBudget')}
          value={budget ? <span className="tabular-nums">{budget}</span> : null}
          fullWidth
        />
        {campaign.status === 'completed' && (
          <Field
            label={t('drydockCampaign.fields.completedAt')}
            value={formatDateTime(campaign.completedAt)}
            fullWidth
          />
        )}
        {campaign.status === 'cancelled' && (
          <>
            <Field
              label={t('drydockCampaign.fields.cancelledAt')}
              value={formatDateTime(campaign.cancelledAt)}
            />
            <Field
              label={t('drydockCampaign.fields.cancelReason')}
              value={
                campaign.cancelReason ? (
                  <p className="whitespace-pre-line">{campaign.cancelReason}</p>
                ) : null
              }
              fullWidth
            />
          </>
        )}
        <Field
          label={t('drydockCampaign.fields.notes')}
          value={
            campaign.notes ? (
              <p className="whitespace-pre-line">{campaign.notes}</p>
            ) : null
          }
          fullWidth
        />
      </Section>

      <div className="lg:col-span-2">
        <LinkedWorkOrdersCard campaignId={campaign.id} />
      </div>
      <div className="lg:col-span-2">
        <LinkedCertificatesCard campaignId={campaign.id} />
      </div>
    </div>
  );
}

export function DrydockCampaignDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const {
    data: campaign,
    isLoading,
    isError,
    error,
    refetch,
  } = useDrydockCampaign(id);

  const startMut = useStartDrydockCampaign(id ?? '');
  const completeMut = useCompleteDrydockCampaign(id ?? '');
  const cancelMut = useCancelDrydockCampaign(id ?? '');

  const [startOpen, setStartOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('drydockCampaign.detail.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('drydockCampaign.detail.loadError');
  })();

  const cancelErrorMessage = (() => {
    if (!cancelMut.error) return null;
    if (cancelMut.error instanceof ApiError) {
      return t(cancelMut.error.key, {
        defaultValue: t('drydockCampaign.transition.cancelError'),
        ...(cancelMut.error.params ?? {}),
      });
    }
    return t('drydockCampaign.transition.cancelError');
  })();

  const canStart = campaign?.status === 'planned';
  const canComplete = campaign?.status === 'in_progress';
  const canCancel =
    campaign?.status === 'planned' || campaign?.status === 'in_progress';
  const canEdit = campaign?.status !== 'cancelled';

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Button
            asChild
            variant="ghost"
            size="icon"
            aria-label={t('common.back')}
          >
            <Link to="/drydock-campaigns">
              <ArrowLeft aria-hidden />
            </Link>
          </Button>
          <h1 className="truncate text-2xl font-semibold">
            {campaign ? campaign.title : t('common.loading')}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canStart && (
            <Button onClick={() => setStartOpen(true)}>
              <Play aria-hidden />
              {t('drydockCampaign.transition.startButton')}
            </Button>
          )}
          {canComplete && (
            <Button onClick={() => setCompleteOpen(true)}>
              <CheckCircle2 aria-hidden />
              {t('drydockCampaign.transition.completeButton')}
            </Button>
          )}
          {canCancel && (
            <Button
              variant="destructive"
              onClick={() => setCancelOpen(true)}
            >
              <Ban aria-hidden />
              {t('drydockCampaign.transition.cancelButton')}
            </Button>
          )}
          {canEdit && campaign && (
            <Button asChild variant="outline">
              <Link to={`/drydock-campaigns/${campaign.id}/edit`}>
                <Pencil aria-hidden />
                {t('common.edit')}
              </Link>
            </Button>
          )}
        </div>
      </header>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('drydockCampaign.detail.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {!campaign && !isLoading && !isError && (
        <EmptyState
          icon={Anchor}
          title={t('drydockCampaign.detail.notFound')}
          description={t('drydockCampaign.detail.notFoundHint')}
        />
      )}

      {campaign && <DetailContent campaign={campaign} />}

      {campaign && (
        <QrCodeDisplay
          entityType="drydock_campaign"
          entityId={campaign.id}
        />
      )}

      {campaign && (
        <>
          <ConfirmDialog
            open={startOpen}
            onOpenChange={setStartOpen}
            title={t('drydockCampaign.transition.startTitle')}
            description={t('drydockCampaign.transition.startDescription')}
            confirmLabel={t('drydockCampaign.transition.startConfirm')}
            loading={startMut.isPending}
            onConfirm={() =>
              startMut.mutate(undefined, {
                onSuccess: () => setStartOpen(false),
              })
            }
          />

          <ConfirmDialog
            open={completeOpen}
            onOpenChange={setCompleteOpen}
            title={t('drydockCampaign.transition.completeTitle')}
            description={t('drydockCampaign.transition.completeDescription')}
            confirmLabel={t('drydockCampaign.transition.completeConfirm')}
            loading={completeMut.isPending}
            onConfirm={() =>
              completeMut.mutate(undefined, {
                onSuccess: () => setCompleteOpen(false),
              })
            }
          />

          <TransitionModal
            open={cancelOpen}
            onOpenChange={(next) => {
              if (!next) cancelMut.reset();
              setCancelOpen(next);
            }}
            title={t('drydockCampaign.transition.cancelTitle')}
            description={t('drydockCampaign.transition.cancelDescription')}
            confirmLabel={t('drydockCampaign.transition.cancelConfirm')}
            variant="destructive"
            requireReason
            loading={cancelMut.isPending}
            errorMessage={cancelErrorMessage}
            onConfirm={({ reason }) => {
              if (!reason) return;
              cancelMut.mutate(
                { reason },
                { onSuccess: () => setCancelOpen(false) },
              );
            }}
          />
        </>
      )}
    </div>
  );
}
